/**
 * O campo de escolha do modal de compromisso ("Lead", "Duração" e "Avisar por e-mail").
 *
 * POR QUE NÃO É A `Selecao` DO CHASSI, e isto foi MEDIDO na tela em 19/09/2026 (suíte `a1.spec.ts`,
 * computador 1280): dentro do `<dialog>` aberto por `showModal`, a lista da `Selecao` aparece, mas
 * o clique na opção cai no campo do modal por cima ("<input ...> intercepts pointer events"). A
 * `Selecao` desenha a lista por portal no `body`, e o `body` fica ABAIXO da camada de cima onde o
 * diálogo modal mora, além de inerte. É defeito da peça do Chassi dentro de modal, e vai para o
 * principal; até lá, este é o ponto ÚNICO onde a exceção mora.
 *
 * O QUE VAI NO LUGAR: o `<select>` nativo, que é exatamente o elemento do legado
 * (`admin.html:2847`, `2869`, `2882`), com a mesma classe de campo do Chassi (`cx-campo`) e o
 * rótulo no mesmo desenho do `Campo`. A lista nativa abre por cima de qualquer camada.
 */

// O identificador único que liga o rótulo ao campo, a referência ao `<select>` e o efeito que roda
// antes da pintura.
import { useId, useLayoutEffect, useRef } from 'react';
// O dicionário: a frase em português é a chave.
import { t } from '@complexity/i18n';

/** Uma opção: o valor gravado e o texto que a pessoa lê. */
export interface OpcaoDeEscolha {
  readonly valor: string;
  readonly rotulo: string;
}

/** O que o campo recebe. */
export interface EscolhaProps {
  // O rótulo acima do campo, com o texto exato do legado.
  readonly rotulo: string;
  // As opções, na ordem do legado.
  readonly opcoes: readonly OpcaoDeEscolha[];
  // O valor escolhido; texto vazio é a opção vazia.
  readonly valor: string;
  // Avisa a escolha nova.
  readonly aoMudar: (valor: string) => void;
  // O texto da opção vazia, quando o campo tem uma.
  readonly vazio?: string;
  // Travado, como todo campo do papel consulta (`admin.html:3394`).
  readonly desabilitado?: boolean;
  // Sem opção nenhuma escolhida, como o `<select>` do legado que recebe um valor que não tem
  // (o lead fora da lista, `leadNoCampo` em `calendario.ts`). O `valor` continua vazio.
  readonly emBranco?: boolean;
}

export function Escolha({ rotulo, opcoes, valor, aoMudar, vazio, desabilitado, emBranco }: EscolhaProps) {
  const id = useId();
  const campo = useRef<HTMLSelectElement>(null);
  // O React não sabe deixar um `<select>` em branco: com valor sem opção, ele marca a PRIMEIRA
  // (medido pela verificação da A1: `selectedIndex 0`, "(selecione)" na tela). E ele volta a marcar
  // a cada desenho do campo, então isto roda depois de TODO desenho, sem lista de dependências,
  // e antes da pintura, para o "(selecione)" nunca piscar.
  useLayoutEffect(() => {
    if (emBranco && campo.current) campo.current.selectedIndex = -1;
  });
  return (
    // O mesmo invólucro e o mesmo rótulo do `Campo` do Chassi, para os quatro campos da linha
    // compacta ficarem do mesmo tamanho e alinhados pela base.
    <div className="flex flex-col gap-cx-1">
      <label htmlFor={id} className="text-cx-sm font-medium text-texto">
        {t(rotulo)}
      </label>
      <select ref={campo} id={id} className="cx-campo" value={valor} disabled={desabilitado} onChange={(evento) => aoMudar(evento.target.value)}>
        {/* A opção vazia primeiro, como no HTML do legado. */}
        {vazio !== undefined && <option value="">{vazio}</option>}
        {opcoes.map((opcao) => (
          <option key={opcao.valor} value={opcao.valor}>
            {opcao.rotulo}
          </option>
        ))}
      </select>
    </div>
  );
}
